import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../shared/config';
import { useAuth } from '../../contexts/AuthContext';
import {
  Stethoscope, Microscope, Plus, LayoutDashboard, FolderOpen, Bell, LogOut, Search,
  ChevronRight, ChevronLeft, ChevronDown, ClipboardPaste, Check, Clock, FileText, Upload, Trash2, X, Loader2,
  CalendarDays, AlertTriangle, CheckCircle2, Lock, User as UserIcon, Download, Send, Settings, KeyRound
} from 'lucide-react';
import MfaManager from '../profile/MfaManager';
import PushNotificationManager from '../notifications/PushNotificationManager';
import { parsePatientColle } from '../../shared/parsePatientColle';
import { hasDroit } from '../../lib/droits';

const STATUT_STYLE = {
  recue: 'bg-blue-50 text-blue-700 border-blue-200',
  en_etude: 'bg-orange-50 text-orange-700 border-orange-200',
  info_requise: 'bg-red-50 text-red-700 border-red-200',
  validee: 'bg-blue-50 text-blue-700 border-blue-200',
  refusee: 'bg-red-50 text-red-600 border-red-200',
  rdv_programme: 'bg-violet-50 text-violet-700 border-violet-200',
  pre_op: 'bg-violet-50 text-violet-700 border-violet-200',
  intervention: 'bg-blue-50 text-blue-700 border-blue-200',
  compte_rendu: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  suivi_post_op: 'bg-orange-50 text-orange-700 border-orange-200',
  terminee: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cloturee: 'bg-gray-100 text-gray-500 border-gray-200',
};
const PRIORITE_STYLE = { urgent: 'bg-red-600 text-white', important: 'bg-orange-500 text-white', normal: 'bg-gray-100 text-gray-500' };
const CAT_LABELS = { rendez_vous: 'Rendez-vous', compte_rendu: 'Comptes rendus', imagerie: 'Imagerie', resultats: 'Résultats', courriers: 'Courriers', administratif: 'Administratif', autre: 'Autres' };

const Badge = ({ d }) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-bold border ${STATUT_STYLE[d.statut] || STATUT_STYLE.recue}`}>
    {d.statut_label}
  </span>
);

const fmtD = (iso) => iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

export default function PortailCorrespondant() {
  const { user, logout } = useAuth();
  const [vue, setVue] = useState('accueil'); // accueil | demandes | nouvelle | dossier
  const [typeDemande, setTypeDemande] = useState('chirurgie');
  const [dossierId, setDossierId] = useState(null);
  const [dash, setDash] = useState(null);
  const [notifs, setNotifs] = useState({ notifications: [], non_lues: 0 });
  const [showNotifs, setShowNotifs] = useState(false);

  const loadDash = () => axios.get(`${API}/correspondants/dashboard`).then(r => setDash(r.data)).catch(() => {});
  const loadNotifs = () => axios.get(`${API}/correspondants/notifications`).then(r => setNotifs(r.data)).catch(() => {});
  useEffect(() => { loadDash(); loadNotifs(); }, []);

  const ouvrirDossier = (id) => { setDossierId(id); setVue('dossier'); };
  const nouvelleDemande = (t) => { setTypeDemande(t); setVue('nouvelle'); };

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="portail-correspondant">
      {/* Topbar bleu nuit */}
      <header className="bg-[#0B1F3A] text-white sticky top-0 z-40">
        <div className="w-full px-4 sm:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#1173D4] flex items-center justify-center"><Stethoscope className="h-4.5 w-4.5 h-5 w-5" /></div>
            <div>
              <p className="text-base font-bold leading-tight text-white">Portail Correspondants</p>
              <p className="text-xs text-blue-200/70 leading-tight">Espace médical sécurisé</p>
            </div>
          </div>
          <nav className="hidden sm:flex items-center gap-1">
            <button onClick={() => { setVue('accueil'); loadDash(); }} className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${vue === 'accueil' ? 'bg-white/15' : 'hover:bg-white/10 text-blue-100/80'}`} data-testid="corr-nav-accueil"><LayoutDashboard className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />Accueil</button>
            <button onClick={() => setVue('demandes')} className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${vue === 'demandes' ? 'bg-white/15' : 'hover:bg-white/10 text-blue-100/80'}`} data-testid="corr-nav-demandes"><FolderOpen className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />Mes demandes</button>
            <button onClick={() => setVue('parametres')} className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${vue === 'parametres' ? 'bg-white/15' : 'hover:bg-white/10 text-blue-100/80'}`} data-testid="corr-nav-parametres"><Settings className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />Paramètres</button>
          </nav>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button onClick={() => { setShowNotifs(v => !v); if (!showNotifs && notifs.non_lues) axios.put(`${API}/correspondants/notifications/lues`).then(loadNotifs); }}
                className="p-2 rounded-lg hover:bg-white/10 relative" data-testid="corr-notifs-btn">
                <Bell className="h-4 w-4" />
                {notifs.non_lues > 0 && <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[11px] font-bold rounded-full h-4 min-w-4 px-0.5 flex items-center justify-center">{notifs.non_lues}</span>}
              </button>
              {showNotifs && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-100 text-gray-800 max-h-80 overflow-y-auto z-50">
                  <p className="px-3 py-2 text-sm font-bold text-gray-500 border-b">Notifications</p>
                  {notifs.notifications.length === 0 ? <p className="p-4 text-sm text-gray-400">Aucune notification</p> :
                    notifs.notifications.map(n => (
                      <button key={n.id} onClick={() => { setShowNotifs(false); if (n.demande_id) ouvrirDossier(n.demande_id); }} className={`w-full text-left px-3 py-2 border-b last:border-0 hover:bg-blue-50/50 ${!n.lu ? 'bg-blue-50/30' : ''}`}>
                        <p className="text-sm">{n.message}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{fmtD(n.date)}</p>
                      </button>
                    ))}
                </div>
              )}
            </div>
            <span className="hidden sm:block text-sm text-blue-100/80 font-medium">Dr {user?.prenom} {user?.nom}</span>
            <button onClick={logout} className="p-2 rounded-lg hover:bg-white/10" title="Se déconnecter" data-testid="corr-logout"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </header>

      <main className="w-full px-4 sm:px-8 py-6 pb-24">
        {vue === 'accueil' && <Accueil user={user} dash={dash} nouvelleDemande={nouvelleDemande} ouvrirDossier={ouvrirDossier} setVue={setVue} />}
        {vue === 'demandes' && <MesDemandes ouvrirDossier={ouvrirDossier} nouvelleDemande={nouvelleDemande} />}
        {vue === 'nouvelle' && <NouvelleDemande type={typeDemande} onDone={(id) => { loadDash(); ouvrirDossier(id); }} onCancel={() => setVue('accueil')} />}
        {vue === 'dossier' && dossierId && <FicheDossier id={dossierId} retour={() => { setVue('demandes'); loadDash(); }} />}
        {vue === 'parametres' && <ParametresCorrespondant />}
      </main>

      {/* Nav mobile */}
      <nav className="sm:hidden fixed bottom-0 inset-x-0 bg-white border-t flex justify-around py-2 z-40">
        <button onClick={() => setVue('accueil')} className={`flex flex-col items-center text-xs font-semibold ${vue === 'accueil' ? 'text-[#1173D4]' : 'text-gray-400'}`}><LayoutDashboard className="h-5 w-5 mb-0.5" />Accueil</button>
        <button onClick={() => setVue('demandes')} className={`flex flex-col items-center text-xs font-semibold ${vue === 'demandes' ? 'text-[#1173D4]' : 'text-gray-400'}`}><FolderOpen className="h-5 w-5 mb-0.5" />Demandes</button>
        <button onClick={() => setVue('parametres')} className={`flex flex-col items-center text-xs font-semibold ${vue === 'parametres' ? 'text-[#1173D4]' : 'text-gray-400'}`}><Settings className="h-5 w-5 mb-0.5" />Paramètres</button>
      </nav>
    </div>
  );
}

/* ---------- Accueil ---------- */
function Accueil({ user, dash, nouvelleDemande, ouvrirDossier, setVue }) {
  const s = dash?.stats || {};
  const cards = [
    { label: 'Demandes en cours', val: s.en_cours ?? '—', color: 'text-[#1173D4]', bg: 'bg-blue-50', icon: FolderOpen },
    { label: 'Rendez-vous programmés', val: s.rdv_programmes ?? '—', color: 'text-violet-600', bg: 'bg-violet-50', icon: CalendarDays },
    { label: 'Actions requises', val: s.actions_requises ?? '—', color: 'text-red-600', bg: 'bg-red-50', icon: AlertTriangle },
    { label: 'Dossiers terminés', val: s.terminees ?? '—', color: 'text-emerald-600', bg: 'bg-emerald-50', icon: CheckCircle2 },
  ];
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-[#0B1F3A]">Bonjour Dr {user?.nom} 👋</h1>
        <p className="text-base text-gray-500 mt-0.5">Voici l'état de vos dossiers.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map(c => (
          <div key={c.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4" data-testid={`corr-stat-${c.label}`}>
            <div className={`w-9 h-9 rounded-xl ${c.bg} ${c.color} flex items-center justify-center mb-2`}><c.icon className="h-4.5 w-4.5 h-5 w-5" /></div>
            <p className={`text-3xl font-bold ${c.color}`}>{c.val}</p>
            <p className="text-sm text-gray-500 font-medium mt-0.5">{c.label}</p>
          </div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <button onClick={() => nouvelleDemande('chirurgie')} className="group bg-[#1173D4] hover:bg-[#0d5cab] text-white rounded-2xl p-5 flex items-center gap-4 shadow-sm transition-colors" data-testid="corr-btn-chirurgie">
          <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center"><Stethoscope className="h-5 w-5" /></div>
          <div className="text-left"><p className="text-lg font-extrabold text-white">+ Demande de chirurgie</p><p className="text-sm text-white/90">Adresser un patient pour une intervention</p></div>
          <ChevronRight className="h-5 w-5 ml-auto opacity-60 group-hover:translate-x-0.5 transition-transform" />
        </button>
        <button onClick={() => nouvelleDemande('examen')} className="group bg-[#0B1F3A] hover:bg-[#132c50] text-white rounded-2xl p-5 flex items-center gap-4 shadow-sm transition-colors" data-testid="corr-btn-examen">
          <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center"><Microscope className="h-5 w-5" /></div>
          <div className="text-left"><p className="text-lg font-extrabold text-white">+ Demande d'examen</p><p className="text-sm text-white/90">Angiographie, échographie, UBM…</p></div>
          <ChevronRight className="h-5 w-5 ml-auto opacity-60 group-hover:translate-x-0.5 transition-transform" />
        </button>
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between px-4 pt-4">
          <h2 className="text-base font-bold text-[#0B1F3A]">Vos demandes récentes</h2>
          <button onClick={() => setVue('demandes')} className="text-sm font-semibold text-[#1173D4] hover:underline" data-testid="corr-voir-tout">Tout voir</button>
        </div>
        <div className="divide-y divide-gray-50 mt-2">
          {(dash?.recentes || []).length === 0 && <p className="px-4 py-6 text-sm text-gray-400">Aucune demande pour le moment — créez votre première demande ci-dessus.</p>}
          {(dash?.recentes || []).map(d => (
            <button key={d.id} onClick={() => ouvrirDossier(d.id)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50/40 text-left transition-colors" data-testid={`corr-recente-${d.numero}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${d.type === 'chirurgie' ? 'bg-blue-50 text-[#1173D4]' : 'bg-slate-100 text-[#0B1F3A]'}`}>
                {d.type === 'chirurgie' ? <Stethoscope className="h-4 w-4" /> : <Microscope className="h-4 w-4" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-base font-semibold text-gray-800 truncate">{d.patient_prenom} {d.patient_nom} <span className="text-xs font-mono text-gray-400 ml-1">{d.numero}</span></p>
                <p className="text-sm text-gray-400">Prochaine étape : {d.prochaine_etape}</p>
              </div>
              {d.priorite !== 'normal' && <span className={`px-1.5 py-0.5 rounded text-[11px] font-bold uppercase ${PRIORITE_STYLE[d.priorite]}`}>{d.priorite}</span>}
              <Badge d={d} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Mes demandes ---------- */
function MesDemandes({ ouvrirDossier, nouvelleDemande }) {
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [fType, setFType] = useState('');
  const [fStatut, setFStatut] = useState('');
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/correspondants/demandes`, { params: { type: fType, statut: fStatut, q } })
      .then(r => setDemandes(r.data)).catch(() => toast.error('Erreur de chargement')).finally(() => setLoading(false));
  }, [q, fType, fStatut]);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold text-[#0B1F3A]">Mes demandes</h1>
        <div className="flex gap-2">
          <button onClick={() => nouvelleDemande('chirurgie')} className="px-3 py-1.5 rounded-lg bg-[#1173D4] text-white text-sm font-bold hover:bg-[#0d5cab]">+ Chirurgie</button>
          <button onClick={() => nouvelleDemande('examen')} className="px-3 py-1.5 rounded-lg bg-[#0B1F3A] text-white text-sm font-bold hover:bg-[#132c50]">+ Examen</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Patient, n° de dossier…"
            className="w-full h-9 pl-8 pr-3 rounded-xl border border-gray-200 bg-white text-base focus:outline-none focus:border-[#1173D4]" data-testid="corr-search" />
        </div>
        <select value={fType} onChange={e => setFType(e.target.value)} className="h-9 px-2 rounded-xl border border-gray-200 bg-white text-sm font-semibold" data-testid="corr-filtre-type">
          <option value="">Tous types</option><option value="chirurgie">Chirurgie</option><option value="examen">Examen</option>
        </select>
        <select value={fStatut} onChange={e => setFStatut(e.target.value)} className="h-9 px-2 rounded-xl border border-gray-200 bg-white text-sm font-semibold" data-testid="corr-filtre-statut">
          <option value="">Tous statuts</option>
          {Object.entries(STATUT_STYLE).map(([k]) => <option key={k} value={k}>{({ recue: 'Reçue', en_etude: "En cours d'étude", info_requise: 'Info requise', validee: 'Validée', refusee: 'Refusée', rdv_programme: 'RDV programmé', pre_op: 'Pré-opératoire', intervention: 'Intervention', compte_rendu: 'Compte rendu', suivi_post_op: 'Suivi post-op', terminee: 'Terminée', cloturee: 'Clôturée' })[k]}</option>)}
        </select>
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#1173D4]" /></div> :
          demandes.length === 0 ? <p className="p-8 text-center text-base text-gray-400">Aucune demande trouvée</p> : (
            <div className="divide-y divide-gray-50">
              {demandes.map(d => (
                <button key={d.id} onClick={() => ouvrirDossier(d.id)} className="w-full grid grid-cols-12 items-center gap-2 px-4 py-3 hover:bg-blue-50/40 text-left transition-colors" data-testid={`corr-demande-${d.numero}`}>
                  <span className="col-span-3 sm:col-span-2 text-sm font-mono text-gray-500">{d.numero}</span>
                  <span className="col-span-5 sm:col-span-3 text-base font-semibold text-gray-800 truncate">{d.patient_prenom} {d.patient_nom}</span>
                  <span className="hidden sm:block col-span-2 text-sm text-gray-500 truncate">{d.type_intervention || (d.type === 'chirurgie' ? 'Chirurgie' : 'Examen')}</span>
                  <span className="hidden sm:block col-span-2 text-sm text-gray-400">{fmtD(d.updated_at)}</span>
                  <span className="col-span-4 sm:col-span-3 flex justify-end"><Badge d={d} /></span>
                </button>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}

/* ---------- Champs réutilisables ---------- */
const Champ = ({ label, value, onChange, requis, type: t = 'text', placeholder, testid }) => (
  <div>
    <label className="text-sm font-semibold text-gray-600">{label}{requis && <span className="text-red-500"> *</span>}</label>
    <input type={t} value={value} onChange={onChange} placeholder={placeholder || ''} data-testid={testid}
      className="w-full h-10 px-3 mt-1 rounded-xl border border-gray-200 bg-white text-base focus:outline-none focus:border-[#1173D4]" />
  </div>
);
const Zone = ({ label, value, onChange, rows = 2, requis, max = 3000 }) => (
  <div>
    <div className="flex justify-between"><label className="text-sm font-semibold text-gray-600">{label}{requis && <span className="text-red-500"> *</span>}</label><span className="text-xs text-gray-300">{value.length}/{max}</span></div>
    <textarea value={value} onChange={onChange} rows={rows} maxLength={max}
      className="w-full px-3 py-2 mt-1 rounded-xl border border-gray-200 bg-white text-base focus:outline-none focus:border-[#1173D4] resize-y" />
  </div>
);

/* Extraction 100% locale par expressions régulières (aucune IA) — parseur partagé */

/* ---------- Nouvelle demande (wizard 5 étapes) ---------- */
function NouvelleDemande({ type, onDone, onCancel }) {
  const chirurgie = type === 'chirurgie';
  const [etape, setEtape] = useState(1);
  const [types, setTypes] = useState([]);
  const [patient, setPatient] = useState({ nom: '', prenom: '', date_naissance: '', matricule: '', telephone: '', email: '', adresse: '' });
  const [medical, setMedical] = useState({ motif: '', diagnostic: '', antecedents: '', traitements: '', allergies: '', prescription: '' });
  const [typeIntervention, setTypeIntervention] = useState('');
  const [infos, setInfos] = useState('');
  const [priorite, setPriorite] = useState('normal');
  const [fichiers, setFichiers] = useState([]);
  const [envoi, setEnvoi] = useState(false);
  const [pasteTxt, setPasteTxt] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { axios.get(`${API}/correspondants/types`).then(r => setTypes(r.data.filter(t => t.categorie === type && t.actif !== false))); }, [type]);

  const etapes = ['Patient', 'Demande', 'Documents', 'Vérification', 'Envoi'];
  const peutContinuer = etape === 1 ? patient.nom.trim() && patient.prenom.trim() : etape === 2 ? !!typeIntervention : true;

  const ajouterFichiers = (list) => {
    const ok = Array.from(list).filter(f => /\.(pdf|jpe?g|png)$/i.test(f.name) && f.size <= 20 * 1024 * 1024);
    if (ok.length !== list.length) toast.error('Seuls les PDF/JPG/PNG de moins de 20 Mo sont acceptés');
    setFichiers(prev => [...prev, ...ok]);
  };

  const envoyer = async () => {
    setEnvoi(true);
    try {
      const r = await axios.post(`${API}/correspondants/demandes`, {
        type, patient, medical, type_intervention: typeIntervention, infos_complementaires: infos, priorite,
      });
      for (const f of fichiers) {
        const fd = new FormData(); fd.append('file', f);
        await axios.post(`${API}/correspondants/demandes/${r.data.id}/documents?categorie=courriers`, fd).catch(() => toast.error(`Échec de l'envoi de ${f.name}`));
      }
      toast.success(`Votre demande a bien été transmise — ${r.data.numero}`);
      onDone(r.data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi"); }
    finally { setEnvoi(false); }
  };

  const remplirDepuisTexte = () => {
    const p = parsePatientColle(pasteTxt);
    const champs = ['nom', 'prenom', 'date_naissance', 'matricule', 'telephone', 'email', 'adresse'];
    const utiles = Object.fromEntries(Object.entries(p).filter(([k]) => champs.includes(k)));
    if (!Object.keys(utiles).length) { toast.error('Aucune information reconnue dans le texte collé'); return; }
    setPatient(prev => ({ ...prev, ...utiles }));
    toast.success(`${Object.keys(utiles).length} champ(s) rempli(s) — vérifiez les informations`);
  };

  return (
    <div className="w-full space-y-4" data-testid="corr-wizard">
      <div className="flex items-center gap-3">
        <button onClick={onCancel} className="p-1.5 rounded-lg hover:bg-gray-100"><ChevronLeft className="h-5 w-5 text-gray-500" /></button>
        <div>
          <h1 className="text-2xl font-bold text-[#0B1F3A]">{chirurgie ? 'Demande de chirurgie' : "Demande d'examen"}</h1>
          <p className="text-sm text-gray-400">Étape {etape} sur 5 — {etapes[etape - 1]}</p>
        </div>
      </div>
      {/* Barre de progression */}
      <div className="flex gap-1.5">
        {etapes.map((s, i) => (
          <div key={s} className="flex-1">
            <div className={`h-1.5 rounded-full ${i < etape ? (chirurgie ? 'bg-[#1173D4]' : 'bg-[#0B1F3A]') : 'bg-gray-200'}`} />
            <p className={`text-[11px] mt-1 font-semibold ${i < etape ? 'text-gray-600' : 'text-gray-300'}`}>{s}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-3">
        {etape === 1 && (
          <div className="grid lg:grid-cols-5 gap-5">
            <div className="lg:col-span-3 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <Champ label="Nom" requis testid="corr-patient-nom" value={patient.nom} onChange={e => setPatient(p => ({ ...p, nom: e.target.value }))} />
                <Champ label="Prénom" requis testid="corr-patient-prenom" value={patient.prenom} onChange={e => setPatient(p => ({ ...p, prenom: e.target.value }))} />
                <Champ label="Date de naissance" type="date" value={patient.date_naissance} onChange={e => setPatient(p => ({ ...p, date_naissance: e.target.value }))} />
                <Champ label="N° de sécurité sociale / matricule" value={patient.matricule} onChange={e => setPatient(p => ({ ...p, matricule: e.target.value }))} />
                <Champ label="Téléphone" value={patient.telephone} onChange={e => setPatient(p => ({ ...p, telephone: e.target.value }))} />
                <Champ label="E-mail (optionnel)" value={patient.email} onChange={e => setPatient(p => ({ ...p, email: e.target.value }))} />
              </div>
              <Champ label="Adresse (optionnel)" value={patient.adresse} onChange={e => setPatient(p => ({ ...p, adresse: e.target.value }))} />
            </div>
            <div className="lg:col-span-2">
              <div className="bg-blue-50/60 border border-blue-100 rounded-2xl p-4 h-full flex flex-col">
                <p className="text-base font-bold text-[#0B1F3A] flex items-center gap-2"><ClipboardPaste className="h-4 w-4 text-[#1173D4]" /> Remplissage rapide</p>
                <p className="text-sm text-gray-500 mt-1">Collez les informations du patient (courrier, fiche, e-mail…) puis cliquez sur « Remplir les champs ».</p>
                <textarea value={pasteTxt} onChange={e => setPasteTxt(e.target.value)} rows={8}
                  placeholder={"Ex :\nNom : LEBLOND\nPrénom : Francis\nNé le 12/03/1958\nMatricule : 1958031212345\nTél : +352 621 123 456"}
                  className="w-full flex-1 px-3 py-2 mt-2 rounded-xl border border-blue-200 bg-white text-sm focus:outline-none focus:border-[#1173D4] resize-y" data-testid="corr-paste-zone" />
                <button onClick={remplirDepuisTexte} disabled={!pasteTxt.trim()}
                  className="mt-2 px-4 py-2 rounded-xl bg-[#1173D4] text-white text-sm font-bold hover:bg-[#0d5cab] disabled:opacity-40 transition-colors" data-testid="corr-paste-fill">
                  Remplir les champs
                </button>
              </div>
            </div>
          </div>
        )}
        {etape === 2 && (
          <>
            <div>
              <label className="text-sm font-semibold text-gray-600">{chirurgie ? 'Chirurgie souhaitée' : "Type d'examen"} <span className="text-red-500">*</span></label>
              <select value={typeIntervention} onChange={e => setTypeIntervention(e.target.value)}
                className="w-full h-11 px-3 mt-1.5 rounded-xl border border-gray-200 bg-white text-base font-semibold text-gray-700 focus:outline-none focus:border-[#1173D4]" data-testid="corr-type-select">
                <option value="">— Sélectionnez {chirurgie ? 'la chirurgie' : "l'examen"} —</option>
                {types.map(t => <option key={t.id} value={t.nom}>{t.nom}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-600">Priorité</label>
              <div className="flex gap-2 mt-1.5">
                {[['normal', 'Normal'], ['important', 'Important'], ['urgent', 'Urgent']].map(([k, l]) => (
                  <button key={k} onClick={() => setPriorite(k)} className={`px-3 py-1.5 rounded-full text-sm font-bold border transition-colors ${priorite === k ? (k === 'urgent' ? 'bg-red-600 text-white border-red-600' : k === 'important' ? 'bg-orange-500 text-white border-orange-500' : 'bg-gray-700 text-white border-gray-700') : 'border-gray-200 text-gray-500 hover:border-gray-300'}`} data-testid={`corr-priorite-${k}`}>{l}</button>
                ))}
              </div>
            </div>
            <button onClick={() => setShowDetails(v => !v)}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors" data-testid="corr-toggle-details">
              <span className="text-sm font-bold text-gray-600">Détails médicaux (optionnel) — motif, diagnostic, antécédents…</span>
              <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
            </button>
            {showDetails && (
              <>
                <Zone label="Motif de la demande" value={medical.motif} onChange={e => setMedical(m => ({ ...m, motif: e.target.value }))} />
                <Zone label={chirurgie ? 'Diagnostic' : 'Prescription / demande'} value={chirurgie ? medical.diagnostic : medical.prescription} onChange={e => setMedical(m => chirurgie ? { ...m, diagnostic: e.target.value } : { ...m, prescription: e.target.value })} />
                {chirurgie && (
                  <div className="grid sm:grid-cols-3 gap-3">
                    <Zone label="Antécédents pertinents" value={medical.antecedents} onChange={e => setMedical(m => ({ ...m, antecedents: e.target.value }))} />
                    <Zone label="Traitements" value={medical.traitements} onChange={e => setMedical(m => ({ ...m, traitements: e.target.value }))} />
                    <Zone label="Allergies" value={medical.allergies} onChange={e => setMedical(m => ({ ...m, allergies: e.target.value }))} />
                  </div>
                )}
                {!chirurgie && <Zone label="Diagnostic" value={medical.diagnostic} onChange={e => setMedical(m => ({ ...m, diagnostic: e.target.value }))} />}
                <Zone label="Informations complémentaires" value={infos} onChange={e => setInfos(e.target.value)} rows={3} max={5000} />
              </>
            )}
          </>
        )}
        {etape === 3 && (
          <>
            <div onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); ajouterFichiers(e.dataTransfer.files); }}
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-gray-200 hover:border-[#1173D4] rounded-2xl p-8 text-center cursor-pointer transition-colors" data-testid="corr-dropzone">
              <Upload className="h-7 w-7 mx-auto text-gray-300 mb-2" />
              <p className="text-base font-semibold text-gray-600">Glissez-déposez vos documents ici</p>
              <p className="text-sm text-gray-400 mt-0.5">ou cliquez pour parcourir — PDF, JPG, PNG (max 20 Mo)</p>
              <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={e => { ajouterFichiers(e.target.files); e.target.value = ''; }} />
            </div>
            {fichiers.map((f, i) => (
              <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2">
                <FileText className="h-4 w-4 text-[#1173D4] shrink-0" />
                <span className="text-sm font-semibold text-gray-700 truncate flex-1">{f.name}</span>
                <span className="text-xs text-gray-400">{(f.size / 1024 / 1024).toFixed(1)} Mo</span>
                <button onClick={() => setFichiers(prev => prev.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            {fichiers.length === 0 && <p className="text-sm text-gray-400 text-center">Aucun document — cette étape est facultative.</p>}
          </>
        )}
        {etape >= 4 && (
          <div className="space-y-3">
            <Recap titre="Patient" lignes={[`${patient.prenom} ${patient.nom}`, patient.date_naissance && `Né(e) le ${patient.date_naissance}`, patient.matricule && `Matricule : ${patient.matricule}`, patient.telephone].filter(Boolean)} />
            <Recap titre="Type de demande" lignes={[`${chirurgie ? 'Chirurgie' : 'Examen'} — ${typeIntervention}`, `Priorité : ${priorite}`]} />
            <Recap titre="Demande" lignes={[medical.motif, medical.diagnostic && `Diagnostic : ${medical.diagnostic}`, infos && `Infos : ${infos}`].filter(Boolean)} />
            <Recap titre="Documents" lignes={fichiers.length ? fichiers.map(f => f.name) : ['Aucun document joint']} />
            <div className="flex items-center gap-2 text-sm text-gray-400 bg-blue-50/50 rounded-xl px-3 py-2">
              <Lock className="h-3.5 w-3.5 text-[#1173D4]" /> Données transmises de manière chiffrée à l'équipe médicale — accès strictement limité à votre dossier.
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between">
        <button onClick={() => etape === 1 ? onCancel() : setEtape(e => e - 1)} className="px-4 py-2 rounded-xl border border-gray-200 bg-white text-base font-semibold text-gray-600 hover:border-gray-300" data-testid="corr-wizard-retour">
          {etape === 1 ? 'Annuler' : 'Modifier / Retour'}
        </button>
        {etape < 4 ? (
          <button onClick={() => setEtape(e => e + 1)} disabled={!peutContinuer}
            className="px-5 py-2 rounded-xl bg-[#1173D4] text-white text-base font-bold hover:bg-[#0d5cab] disabled:opacity-40" data-testid="corr-wizard-suivant">
            Continuer <ChevronRight className="h-4 w-4 inline -mt-0.5" />
          </button>
        ) : (
          <button onClick={envoyer} disabled={envoi}
            className="px-5 py-2 rounded-xl bg-emerald-600 text-white text-base font-bold hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2" data-testid="corr-wizard-envoyer">
            {envoi ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer la demande
          </button>
        )}
      </div>
    </div>
  );
}

/* ---------- Panneau actions équipe (utilisé côté administration) ---------- */
const STATUT_OPTIONS = [['recue', 'Demande reçue'], ['en_etude', "En cours d'étude"], ['info_requise', 'Informations requises'], ['validee', 'Validée'], ['refusee', 'Refusée'], ['rdv_programme', 'RDV programmé'], ['pre_op', 'Pré-opératoire'], ['intervention', 'Intervention / Examen'], ['compte_rendu', 'Compte rendu disponible'], ['suivi_post_op', 'Suivi post-opératoire'], ['terminee', 'Terminée'], ['cloturee', 'Clôturée']];

function PanneauAdmin({ d, reload }) {
  const { user } = useAuth();
  const [statut, setStatut] = useState(d.statut);
  const [note, setNote] = useState('');
  const [rdv, setRdv] = useState({ date: '', heure: '', lieu: '', service: '', medecin: '', medecin_id: '', type: '' });
  const [medecins, setMedecins] = useState([]);
  useEffect(() => { axios.get(`${API}/users/by-role/Médecin`).then(r => setMedecins(r.data)).catch(() => {}); }, []);
  const choisirMedecin = (id) => {
    const m = medecins.find(x => x.id === id);
    setRdv(r => ({ ...r, medecin_id: id, medecin: m ? `Dr ${m.prenom} ${m.nom}` : '' }));
  };
  const [busy, setBusy] = useState('');
  const act = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); toast.success(msg); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(''); }
  };
  return (
    <div className="bg-[#0B1F3A] rounded-2xl p-4 text-white space-y-3" data-testid="corr-admin-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm font-bold uppercase text-blue-200/70">Actions équipe {d.assigne_a && <span className="normal-case font-normal">— pris en charge par {d.assigne_a}</span>}</p>
        {!d.assigne_a && d.statut !== 'cloturee' && (
          <button onClick={() => act('pec', () => axios.post(`${API}/correspondants/demandes/${d.id}/prise-en-charge`), 'Dossier pris en charge')}
            disabled={!!busy} className="px-3 py-1.5 rounded-lg bg-[#1173D4] text-sm font-bold hover:bg-[#0d5cab] disabled:opacity-50" data-testid="corr-pec-btn">
            {busy === 'pec' ? '…' : 'Prendre en charge'}
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <p className="text-xs text-blue-200/60 mb-1">Statut</p>
          <select value={statut} onChange={e => setStatut(e.target.value)} className="h-8 rounded-lg text-sm font-semibold text-gray-800 px-1" data-testid="corr-statut-select">
            {STATUT_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note (visible par le correspondant)…"
          className="flex-1 min-w-40 h-8 rounded-lg px-2 text-sm text-gray-800" data-testid="corr-statut-note" />
        <button onClick={() => act('st', () => axios.put(`${API}/correspondants/demandes/${d.id}/statut`, { statut, note }), 'Statut mis à jour — correspondant notifié')}
          disabled={!!busy || statut === d.statut && !note} className="px-3 h-8 rounded-lg bg-emerald-600 text-sm font-bold hover:bg-emerald-700 disabled:opacity-40" data-testid="corr-statut-apply">
          Appliquer
        </button>
      </div>
      <div className="flex flex-wrap items-end gap-2 border-t border-white/10 pt-2">
        <p className="text-xs text-blue-200/60 w-full -mb-1">Programmer un rendez-vous</p>
        <input type="date" value={rdv.date} onChange={e => setRdv(r => ({ ...r, date: e.target.value }))} className="h-8 rounded-lg px-2 text-sm text-gray-800" data-testid="corr-rdv-date" />
        <input type="time" value={rdv.heure} onChange={e => setRdv(r => ({ ...r, heure: e.target.value }))} className="h-8 rounded-lg px-2 text-sm text-gray-800" />
        <input value={rdv.type} onChange={e => setRdv(r => ({ ...r, type: e.target.value }))} placeholder="Type (consultation pré-op…)" className="h-8 rounded-lg px-2 text-sm text-gray-800 flex-1 min-w-32" />
        <input value={rdv.lieu} onChange={e => setRdv(r => ({ ...r, lieu: e.target.value }))} placeholder="Lieu" className="h-8 rounded-lg px-2 text-sm text-gray-800 w-28" />
        <select value={rdv.medecin_id} onChange={e => choisirMedecin(e.target.value)} className="h-8 rounded-lg px-2 text-sm text-gray-800 w-40" data-testid="corr-rdv-medecin">
          <option value="">Médecin…</option>
          {medecins.map(m => <option key={m.id} value={m.id}>Dr {m.prenom} {m.nom}</option>)}
        </select>
        <button onClick={() => act('rdv', () => axios.post(`${API}/correspondants/demandes/${d.id}/rdv`, rdv).then(() => setRdv({ date: '', heure: '', lieu: '', service: '', medecin: '', medecin_id: '', type: '' })), 'Rendez-vous ajouté — correspondant notifié')}
          disabled={!!busy || !rdv.date} className="px-3 h-8 rounded-lg bg-violet-500 text-sm font-bold hover:bg-violet-600 disabled:opacity-40" data-testid="corr-rdv-add">
          Ajouter le RDV
        </button>
      </div>
      {hasDroit(user, 'correspondants', 'purger') && d.statut === 'cloturee' && !d.purge && (
        <div className="border-t border-white/10 pt-2 flex items-center justify-between">
          <p className="text-xs text-blue-200/60">Clôture définitive : supprime les documents et efface les données médicales (RGPD). Irréversible.</p>
          <button onClick={() => { if (window.confirm(`Clôture DÉFINITIVE du dossier ${d.numero} : documents et données médicales supprimés. Continuer ?`)) act('purge', () => axios.delete(`${API}/correspondants/demandes/${d.id}/purge`), 'Dossier purgé définitivement'); }}
            disabled={!!busy} className="px-3 py-1.5 rounded-lg bg-red-600 text-sm font-bold hover:bg-red-700 disabled:opacity-50" data-testid="corr-purge-btn">
            Clôture définitive
          </button>
        </div>
      )}
      {d.purge && <p className="text-xs text-red-300">🔒 Dossier purgé le {fmtD(d.purge_at)} — données médicales effacées.</p>}
    </div>
  );
}

/* ---------- Paramètres du correspondant ---------- */
function ChangerMotDePasse() {
  const [actuel, setActuel] = useState('');
  const [nouveau, setNouveau] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const changer = async () => {
    if (nouveau !== confirm) { toast.error('Les deux mots de passe ne correspondent pas'); return; }
    if (nouveau.length < 12) { toast.error('Le mot de passe doit contenir au moins 12 caractères, avec lettres et chiffres ou symboles'); return; }
    setBusy(true);
    try {
      await axios.put(`${API}/users/me/password`, { current_password: actuel, new_password: nouveau });
      toast.success('Mot de passe mis à jour');
      setActuel(''); setNouveau(''); setConfirm('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5" data-testid="corr-mdp-card">
      <p className="text-base font-bold text-[#0B1F3A] flex items-center gap-2"><KeyRound className="h-4 w-4 text-[#1173D4]" /> Changer mon mot de passe</p>
      <div className="grid sm:grid-cols-3 gap-3 mt-3">
        <Champ label="Mot de passe actuel" type="password" requis value={actuel} onChange={e => setActuel(e.target.value)} testid="corr-mdp-actuel" />
        <Champ label="Nouveau mot de passe" type="password" requis value={nouveau} onChange={e => setNouveau(e.target.value)} testid="corr-mdp-nouveau" />
        <Champ label="Confirmer le nouveau" type="password" requis value={confirm} onChange={e => setConfirm(e.target.value)} testid="corr-mdp-confirm" />
      </div>
      <button onClick={changer} disabled={busy || !actuel || !nouveau || !confirm}
        className="mt-3 px-4 py-2 rounded-xl bg-[#1173D4] text-white text-sm font-bold hover:bg-[#0d5cab] disabled:opacity-40 transition-colors" data-testid="corr-mdp-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : 'Mettre à jour le mot de passe'}
      </button>
    </div>
  );
}

function ParametresCorrespondant() {
  return (
    <div className="space-y-6" data-testid="corr-parametres">
      <div>
        <h1 className="text-3xl font-bold text-[#0B1F3A]">Paramètres</h1>
        <p className="text-base text-gray-500 mt-0.5">Sécurité de votre compte et préférences de notifications.</p>
      </div>
      <div className="grid lg:grid-cols-2 gap-5 items-start">
        <div className="space-y-5">
          <ChangerMotDePasse />
          <div data-testid="corr-mfa-section"><MfaManager /></div>
        </div>
        <div data-testid="corr-push-section"><PushNotificationManager /></div>
      </div>
    </div>
  );
}

const Recap = ({ titre, lignes }) => (
  <div className="border border-gray-100 rounded-xl p-3">
    <p className="text-xs font-bold uppercase text-gray-400 mb-1">{titre}</p>
    {lignes.map((l, i) => <p key={i} className="text-base text-gray-700">{l}</p>)}
  </div>
);

/* ---------- Fiche dossier (partagée portail + administration) ---------- */
export function FicheDossier({ id, retour, admin = false, onChanged }) {
  const [d, setD] = useState(null);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmCloture, setConfirmCloture] = useState(false);
  const fileRef = useRef(null);
  const [catUpload, setCatUpload] = useState('courriers');

  const load = () => axios.get(`${API}/correspondants/demandes/${id}`).then(r => setD(r.data)).catch(() => { toast.error('Dossier inaccessible'); retour(); });
  useEffect(() => { load(); }, [id]);

  if (!d) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#1173D4]" /></div>;
  const cloture = d.statut === 'cloturee';
  const idxStatut = d.statuts_parcours.findIndex(s => s.key === d.statut);

  const download = async (doc) => {
    try {
      const r = await axios.get(`${API}/correspondants/documents/${doc.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
    } catch { toast.error('Téléchargement impossible'); }
  };

  const upload = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      await axios.post(`${API}/correspondants/demandes/${id}/documents?categorie=${catUpload}`, fd);
      toast.success('Le document a été ajouté.');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setUploading(false); }
  };

  const envoyerNote = async () => {
    if (!note.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/correspondants/demandes/${id}/infos`, { texte: note });
      setNote(''); toast.success('Information transmise'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSending(false); }
  };

  const cloturer = async () => {
    try {
      await axios.put(`${API}/correspondants/demandes/${id}/cloturer`, {});
      toast.success('Dossier clôturé'); setConfirmCloture(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="space-y-4" data-testid="corr-fiche-dossier">
      <div className="flex items-center gap-3">
        <button onClick={retour} className="p-1.5 rounded-lg hover:bg-gray-100"><ChevronLeft className="h-5 w-5 text-gray-500" /></button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold text-[#0B1F3A]">{d.patient.prenom} {d.patient.nom}</h1>
            {d.priorite !== 'normal' && <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${PRIORITE_STYLE[d.priorite]}`}>{d.priorite}</span>}
          </div>
          <p className="text-sm text-gray-400">{d.numero} · {d.patient.date_naissance && `Né(e) le ${d.patient.date_naissance} · `}{d.patient.telephone}</p>
        </div>
        {!cloture && (
          <button onClick={() => setConfirmCloture(true)} className="px-3 py-1.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-500 hover:border-red-300 hover:text-red-600 transition-colors" data-testid="corr-cloturer-btn">
            Clôturer le dossier
          </button>
        )}
      </div>

      {/* Bandeau statut */}
      <div className={`rounded-2xl border p-4 ${STATUT_STYLE[d.statut]}`} data-testid="corr-bandeau-statut">
        <p className="text-xs font-bold uppercase opacity-60 text-inherit">Statut actuel</p>
        <p className="text-xl font-bold text-inherit">{d.statut_label}</p>
        {d.prochaine_etape && d.prochaine_etape !== '—' && <p className="text-sm mt-0.5 opacity-80 text-inherit">Prochaine étape : {d.prochaine_etape}</p>}
        <p className="text-xs mt-1 opacity-60 text-inherit">Dernière mise à jour : {fmtD(d.updated_at)}</p>
      </div>

      {/* Panneau d'actions équipe (administration) */}
      {admin && <PanneauAdmin d={d} reload={() => { load(); onChanged && onChanged(); }} />}

      {/* Rendez-vous */}
      {(d.rdv || []).length > 0 && (
        <div className="bg-violet-50 border border-violet-200 rounded-2xl p-4" data-testid="corr-rdv-card">
          <p className="text-xs font-bold uppercase text-violet-500 mb-1">Prochain rendez-vous</p>
          {[...d.rdv].reverse().slice(0, 2).map(r => (
            <div key={r.id} className="text-base text-violet-900 mb-1">
              <span className="font-bold">{r.type || 'Rendez-vous'}</span> — {r.date}{r.heure && ` à ${r.heure}`}
              {(r.lieu || r.service || r.medecin) && <span className="text-sm text-violet-600 block">{[r.lieu, r.service, r.medecin].filter(Boolean).join(' · ')}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Parcours */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 overflow-x-auto">
        <div className="flex items-center min-w-max gap-0">
          {d.statuts_parcours.map((s, i) => (
            <React.Fragment key={s.key}>
              {i > 0 && <div className={`h-0.5 w-6 ${i <= idxStatut ? 'bg-[#1173D4]' : 'bg-gray-200'}`} />}
              <div className="flex flex-col items-center w-20">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i < idxStatut ? 'bg-[#1173D4] text-white' : i === idxStatut ? 'bg-[#0B1F3A] text-white ring-4 ring-blue-100' : 'bg-gray-100 text-gray-400'}`}>
                  {i < idxStatut ? <Check className="h-3 w-3" /> : i + 1}
                </div>
                <p className={`text-[11px] text-center mt-1 leading-tight font-semibold ${i <= idxStatut ? 'text-gray-700' : 'text-gray-300'}`}>{s.label}</p>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Timeline */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <h2 className="text-base font-bold text-[#0B1F3A] mb-3">Historique du dossier</h2>
          <div className="space-y-0">
            {[...d.timeline].reverse().map((ev, i) => (
              <div key={ev.id} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className={`w-2.5 h-2.5 rounded-full mt-1 ${i === 0 ? 'bg-[#1173D4]' : 'bg-gray-300'}`} />
                  {i < d.timeline.length - 1 && <div className="w-px flex-1 bg-gray-200" />}
                </div>
                <div className="pb-4 min-w-0">
                  <p className="text-sm font-bold text-gray-800">{ev.titre}</p>
                  {ev.description && <p className="text-sm text-gray-500 mt-0.5 whitespace-pre-wrap">{ev.description}</p>}
                  <p className="text-xs text-gray-400 mt-0.5">{fmtD(ev.date)} — {ev.auteur}</p>
                </div>
              </div>
            ))}
          </div>
          {!cloture && (
            <div className="mt-2 flex gap-2">
              <input value={note} onChange={e => setNote(e.target.value)} placeholder="Ajouter une information complémentaire…"
                className="flex-1 h-9 px-3 rounded-xl border border-gray-200 text-base focus:outline-none focus:border-[#1173D4]" data-testid="corr-note-input" />
              <button onClick={envoyerNote} disabled={sending || !note.trim()} className="px-3 rounded-xl bg-[#1173D4] text-white text-sm font-bold disabled:opacity-40" data-testid="corr-note-send">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          )}
        </div>

        {/* Documents + infos médicales */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-base font-bold text-[#0B1F3A]">Documents</h2>
              {!cloture && (
                <div className="flex items-center gap-1.5">
                  <select value={catUpload} onChange={e => setCatUpload(e.target.value)} className="h-7 text-xs border rounded-lg px-1 bg-white font-semibold text-gray-600">
                    {Object.entries(CAT_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                  <button onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="px-2.5 py-1 rounded-lg bg-[#1173D4] text-white text-xs font-bold hover:bg-[#0d5cab] disabled:opacity-50 flex items-center gap-1" data-testid="corr-doc-add">
                    {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Ajouter
                  </button>
                  <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={e => { upload(e.target.files?.[0]); e.target.value = ''; }} />
                </div>
              )}
            </div>
            {d.documents.length === 0 ? <p className="text-sm text-gray-400 py-3">Aucun document pour le moment.</p> : (
              <div className="space-y-1.5">
                {d.documents.map(doc => (
                  <div key={doc.id} className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2">
                    <FileText className={`h-4 w-4 shrink-0 ${doc.type === 'pdf' ? 'text-[#1173D4]' : 'text-violet-500'}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-700 truncate">{doc.nom}</p>
                      <p className="text-xs text-gray-400">{CAT_LABELS[doc.categorie] || doc.categorie} · {(doc.taille / 1024 / 1024).toFixed(1)} Mo · {fmtD(doc.date)} · {doc.auteur}</p>
                    </div>
                    <button onClick={() => download(doc)} className="p-1.5 rounded-lg text-gray-400 hover:text-[#1173D4] hover:bg-blue-50" title="Télécharger" data-testid={`corr-doc-dl-${doc.id}`}>
                      <Download className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
            <h2 className="text-base font-bold text-[#0B1F3A] mb-2">Demande</h2>
            <div className="space-y-1.5 text-sm text-gray-600">
              <p><span className="font-bold text-gray-400 uppercase text-[11px]">Type</span><br />{d.type === 'chirurgie' ? 'Chirurgie' : 'Examen'} — {d.type_intervention}</p>
              <p><span className="font-bold text-gray-400 uppercase text-[11px]">Motif</span><br />{d.medical.motif}</p>
              {d.medical.diagnostic && <p><span className="font-bold text-gray-400 uppercase text-[11px]">Diagnostic</span><br />{d.medical.diagnostic}</p>}
              {d.medical.allergies && <p><span className="font-bold text-gray-400 uppercase text-[11px]">Allergies</span><br />{d.medical.allergies}</p>}
              {d.infos_complementaires && <p><span className="font-bold text-gray-400 uppercase text-[11px]">Informations complémentaires</span><br />{d.infos_complementaires}</p>}
            </div>
          </div>
        </div>
      </div>

      {cloture && (
        <div className="bg-gray-100 border border-gray-200 rounded-2xl p-4 text-center">
          <Lock className="h-5 w-5 mx-auto text-gray-400 mb-1" />
          <p className="text-base font-bold text-gray-600">Dossier clôturé le {fmtD(d.cloture_at)}</p>
          <p className="text-sm text-gray-400">Consultation et téléchargement toujours possibles — modification désactivée.</p>
        </div>
      )}

      {confirmCloture && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setConfirmCloture(false)}>
          <div className="bg-white rounded-2xl p-5 max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-[#0B1F3A]">Clôturer ce dossier ?</h3>
            <p className="text-sm text-gray-500 mt-1">Le dossier {d.numero} passera en lecture seule. Vous pourrez toujours consulter et télécharger les documents.</p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setConfirmCloture(false)} className="px-3 py-1.5 rounded-xl border text-sm font-semibold text-gray-600">Annuler</button>
              <button onClick={cloturer} className="px-3 py-1.5 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700" data-testid="corr-cloturer-confirm">Clôturer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
